import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Download, KeyRound, Pause, Play, RefreshCw, RotateCcw, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/components/rbac/use-permission';
import { P } from '@/lib/rbac/permissions.generated';
import {
  billingApi,
  saveBlob,
  type BillingEntity,
  type EbicsConnection,
  type EbicsConnectionInput,
  type EbicsOverview,
} from '@/lib/api/billing';
import { EBICS_STATUS, formatIsoDate } from '@/lib/billing';

const STEPS: Array<{ status: EbicsConnection['status'] | 'none'; title: string }> = [
  { status: 'none', title: 'Zugangsdaten der Bank' },
  { status: 'new', title: 'Schlüssel senden' },
  { status: 'keys_sent', title: 'INI-Brief an die Bank' },
  { status: 'bank_keys_pending', title: 'Bankschlüssel prüfen' },
  { status: 'active', title: 'Aktiv' },
];

const ORDER: Record<string, number> = { none: 0, new: 1, keys_sent: 2, bank_keys_pending: 3, active: 4, suspended: 4 };

function Hashes({ hashes }: { hashes: Record<string, string> }) {
  const entries = Object.entries(hashes);
  if (entries.length === 0) return null;
  return (
    <dl className="space-y-2 rounded-md border bg-muted/30 p-3 font-mono text-xs">
      {entries.map(([version, hash]) => (
        <div key={version}>
          <dt className="font-sans text-xs font-medium text-muted-foreground">{version === 'raw' ? 'Antwort der Bank' : version}</dt>
          <dd className="break-all">{hash}</dd>
        </div>
      ))}
    </dl>
  );
}

interface Props {
  entity: BillingEntity;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

/** Geführte EBICS-Einrichtung (ADR-0003, Phase 4): Zugang → Schlüssel → INI-Brief → Bankschlüssel → aktiv. */
export default function EbicsSetupDialog({ entity, onClose, onChanged }: Props) {
  const canFetch = usePermission(P.buchhaltung.finanzdaten_und_zahlungen.zahlung_erfassen);
  const [overview, setOverview] = useState<EbicsOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<EbicsConnectionInput>({
    ebicsVersion: 'H005',
    bankUrl: '',
    bankName: '',
    hostId: '',
    partnerId: '',
    userId: '',
    systemId: '',
  });
  const [hashesConfirmed, setHashesConfirmed] = useState(false);
  const [useForTransport, setUseForTransport] = useState(true);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await billingApi.getEbics(entity.id);
      setOverview({ ...data, transfers: Array.isArray(data?.transfers) ? data.transfers : [] });
      if (data?.connection) {
        const c = data.connection;
        setForm({
          ebicsVersion: c.ebicsVersion,
          bankUrl: c.bankUrl,
          bankName: c.bankName,
          hostId: c.hostId,
          partnerId: c.partnerId,
          userId: c.userId,
          systemId: c.systemId ?? '',
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'EBICS-Zugang konnte nicht geladen werden');
    }
  }, [entity.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      toast.success(message);
      await load();
      await onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Aktion fehlgeschlagen');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const connection = overview?.connection ?? null;
  const status = connection?.status ?? 'none';
  const editable = status === 'none' || status === 'new';
  const field = (key: keyof EbicsConnectionInput) => ({
    value: (form[key] as string | undefined) ?? '',
    disabled: !editable || busy,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: event.target.value }),
  });
  const formComplete = Boolean(
    form.bankUrl.trim().startsWith('https://') && form.bankName.trim() && form.hostId.trim() && form.partnerId.trim() && form.userId.trim(),
  );

  const downloadLetter = async () => {
    if (!connection) return;
    try {
      const { blob, filename } = await billingApi.downloadIniLetter(connection.id);
      saveBlob(blob, filename);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'INI-Brief konnte nicht erstellt werden');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3">
            EBICS – {entity.name}
            {connection && (
              <Badge variant={EBICS_STATUS[connection.status]?.variant ?? 'outline'} size="sm">
                {EBICS_STATUS[connection.status]?.label ?? connection.status}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Sidigo überträgt Zahlungsläufe mit Transport-Signatur und ruft Kontoauszüge ab. Freigegeben werden Aufträge
            weiterhin in der Banking-App (verteilte Unterschrift). Die Schlüssel liegen verschlüsselt in Sidigo.
          </DialogDescription>
        </DialogHeader>

        {!overview ? (
          <LoadingState message="EBICS-Zugang wird geladen …" />
        ) : (
          <div className="space-y-6">
            <ol className="grid gap-2 text-sm sm:grid-cols-5">
              {STEPS.map((step, index) => {
                const done = ORDER[status] > index || status === 'active';
                const current = ORDER[status] === index;
                return (
                  <li
                    key={step.title}
                    className={`rounded-md border px-2 py-1.5 ${current ? 'border-primary bg-primary/10 font-medium' : done ? 'text-muted-foreground' : 'text-muted-foreground/70'}`}
                  >
                    {index + 1}. {step.title}
                  </li>
                );
              })}
            </ol>

            {connection?.lastError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                {connection.lastError}
              </div>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Zugangsdaten (aus dem EBICS-Vertrag der Bank)</h3>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="ebics-url">EBICS-URL</Label>
                  <Input id="ebics-url" placeholder="https://ebics.bank.de/ebicsweb/ebicsweb" {...field('bankUrl')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ebics-bank">Name der Bank</Label>
                  <Input id="ebics-bank" placeholder="steht auf dem INI-Brief" {...field('bankName')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ebics-version">EBICS-Version</Label>
                  <Select
                    value={form.ebicsVersion}
                    onValueChange={(value) => setForm({ ...form, ebicsVersion: value as 'H005' | 'H004' })}
                    disabled={!editable || busy}
                  >
                    <SelectTrigger id="ebics-version">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="H005">EBICS 3.0 (H005)</SelectItem>
                      <SelectItem value="H004">EBICS 2.5 (H004)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ebics-host">Host-ID</Label>
                  <Input id="ebics-host" {...field('hostId')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ebics-partner">Kunden-ID</Label>
                  <Input id="ebics-partner" {...field('partnerId')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ebics-user">Teilnehmer-ID</Label>
                  <Input id="ebics-user" {...field('userId')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ebics-system">System-ID (optional)</Label>
                  <Input id="ebics-system" {...field('systemId')} />
                </div>
              </div>
              {editable && (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="outline"
                    disabled={busy || !formComplete}
                    onClick={() => void act(() => billingApi.saveEbics(entity.id, form), 'Zugangsdaten gespeichert')}
                  >
                    Zugangsdaten speichern
                  </Button>
                  {connection && (
                    <Button
                      disabled={busy}
                      onClick={() =>
                        void act(() => billingApi.ebicsSendKeys(connection.id), 'Schlüssel erzeugt und an die Bank gesendet')
                      }
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Schlüssel erzeugen und senden
                    </Button>
                  )}
                </div>
              )}
            </section>

            {connection && ORDER[status] >= 2 && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">INI-Brief</h3>
                <p className="text-sm text-muted-foreground">
                  Ausdrucken, vom Kontoinhaber unterschreiben lassen und an die Bank schicken. Die Bank schaltet den Zugang
                  frei, wenn die Hashwerte im Brief zu den übertragenen Schlüsseln passen.
                </p>
                <Hashes hashes={connection.userKeyHashes} />
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" disabled={busy} onClick={() => void downloadLetter()}>
                    <Download className="mr-2 h-4 w-4" />
                    INI-Brief (PDF)
                  </Button>
                  {(status === 'keys_sent' || status === 'bank_keys_pending') && (
                    <Button
                      disabled={busy}
                      onClick={() => void act(() => billingApi.ebicsFetchBankKeys(connection.id), 'Bankschlüssel abgerufen')}
                    >
                      <KeyRound className="mr-2 h-4 w-4" />
                      Bankschlüssel abrufen
                    </Button>
                  )}
                </div>
              </section>
            )}

            {connection && status === 'bank_keys_pending' && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">Bankschlüssel prüfen</h3>
                <p className="text-sm text-muted-foreground">
                  Vergleiche diese Hashwerte mit dem Schreiben oder der Website der Bank. Nur bei exakter Übereinstimmung
                  aktivieren – sonst ist nicht sicher, dass Sidigo mit der echten Bank spricht.
                </p>
                <Hashes hashes={connection.bankKeyHashes} />
                <label className="flex items-start gap-3 text-sm">
                  <Checkbox checked={hashesConfirmed} onCheckedChange={(checked) => setHashesConfirmed(checked === true)} />
                  Die Hashwerte stimmen mit den Angaben der Bank überein.
                </label>
                <label className="flex items-start gap-3 text-sm">
                  <Checkbox checked={useForTransport} onCheckedChange={(checked) => setUseForTransport(checked === true)} />
                  Zahlungsläufe dieser Einheit ab jetzt per EBICS übertragen (statt Datei-Upload).
                </label>
                <div className="flex justify-end">
                  <Button
                    disabled={busy || !hashesConfirmed}
                    onClick={() =>
                      void act(
                        () => billingApi.ebicsActivate(connection.id, { hashesConfirmed, useForTransport }),
                        'EBICS-Zugang aktiviert',
                      )
                    }
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Aktivieren
                  </Button>
                </div>
              </section>
            )}

            {connection && (status === 'active' || status === 'suspended') && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">Betrieb</h3>
                <p className="text-sm text-muted-foreground">
                  Aktiv seit {formatIsoDate(connection.activatedAt)} · Übertragungsweg:{' '}
                  {overview.sepaTransport === 'ebics' ? 'EBICS' : 'Datei-Upload'} · letzter Abruf{' '}
                  {connection.lastFetchAt ? new Date(connection.lastFetchAt).toLocaleString('de-DE') : '–'}. Kontoauszüge
                  werden werktags morgens und nachmittags automatisch abgerufen.
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  {status === 'active' ? (
                    <>
                      {canFetch && (
                        <Button
                          variant="outline"
                          disabled={busy}
                          onClick={() =>
                            void act(async () => {
                              const result = await billingApi.ebicsFetch(connection.id);
                              if (result.statements.error) throw new Error(result.statements.error);
                              return result;
                            }, 'Kontoauszüge abgerufen')
                          }
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Jetzt abrufen
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() => void act(() => billingApi.ebicsSuspend(connection.id), 'EBICS pausiert')}
                      >
                        <Pause className="mr-2 h-4 w-4" />
                        Pausieren
                      </Button>
                    </>
                  ) : (
                    <Button disabled={busy} onClick={() => void act(() => billingApi.ebicsResume(connection.id), 'EBICS fortgesetzt')}>
                      <Play className="mr-2 h-4 w-4" />
                      Fortsetzen
                    </Button>
                  )}
                </div>
              </section>
            )}

            {overview.transfers.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">Protokoll</h3>
                <div className="max-h-64 overflow-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Zeit</TableHead>
                        <TableHead>Auftrag</TableHead>
                        <TableHead>OrderID</TableHead>
                        <TableHead>Ergebnis</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overview.transfers.map((transfer) => (
                        <TableRow key={transfer.id}>
                          <TableCell className="whitespace-nowrap text-xs tabular-nums">
                            {transfer.createdAt ? new Date(transfer.createdAt).toLocaleString('de-DE') : '–'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {transfer.orderType}
                            {transfer.service ? <span className="text-muted-foreground"> · {transfer.service}</span> : null}
                          </TableCell>
                          <TableCell className="text-xs tabular-nums">{transfer.orderId ?? '–'}</TableCell>
                          <TableCell className="text-sm">
                            <Badge
                              variant={transfer.status === 'ok' ? 'success' : transfer.status === 'no_data' ? 'secondary' : 'destructive'}
                              size="sm"
                            >
                              {transfer.status === 'ok' ? 'OK' : transfer.status === 'no_data' ? 'Keine Daten' : 'Fehler'}
                            </Badge>
                            {(transfer.returnCode || transfer.message) && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {[transfer.returnCode, transfer.message].filter(Boolean).join(' – ')}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {connection && status !== 'new' ? (
            <Button variant="outline" className="text-destructive" disabled={busy} onClick={() => setConfirmReset(true)}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Zugang zurücksetzen
            </Button>
          ) : (
            <span />
          )}
          <Button variant="outline" onClick={onClose}>
            Schließen
          </Button>
        </DialogFooter>

        {confirmReset && connection && (
          <Dialog open onOpenChange={(open) => !open && setConfirmReset(false)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>EBICS-Zugang zurücksetzen?</DialogTitle>
                <DialogDescription>
                  Die Schlüssel werden gelöscht, Zahlungsläufe gehen wieder per Datei-Upload. Für einen neuen Zugang muss
                  die Bank die neuen Schlüssel erneut per INI-Brief freischalten.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmReset(false)} disabled={busy}>
                  Zurück
                </Button>
                <Button
                  variant="destructive"
                  disabled={busy}
                  onClick={async () => {
                    await act(() => billingApi.ebicsReset(connection.id), 'EBICS-Zugang zurückgesetzt');
                    setConfirmReset(false);
                  }}
                >
                  Zurücksetzen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
